import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatAdminLabel, formatAdminValue, formatDisplayValue } from "@/lib/adminFormatters";
import { api, getErrorMessage } from "@/lib/api";


const tabConfig = [
  ["applications", "Applications"],
  ["admin_actions", "Admin Actions"],
  ["status_changes", "Status Changes"],
  ["emails", "Emails"],
  ["security_access", "Security / Access"],
  ["high_risk", "High-Risk Actions"],
];


const formatDateTime = (value) => value ? new Date(value).toLocaleString() : "—";


const renderWorkflow = (row) => [row.application_status, row.payment_status, row.approval_status].filter(Boolean).map((item) => formatAdminLabel(item)).join(" • ") || "—";

const renderDetailItems = (detail) => {
  if (!detail || typeof detail !== "object") return [];
  return Object.entries(detail)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return [{ label: formatAdminLabel(key), value: value.map((item) => formatDisplayValue(item)).join(", ") }];
      }
      if (typeof value === "object") {
        return Object.entries(value)
          .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined && nestedValue !== "")
          .map(([nestedKey, nestedValue]) => ({
            label: `${formatAdminLabel(key)} · ${formatAdminLabel(nestedKey)}`,
            value: formatDisplayValue(nestedValue),
          }));
      }
      return [{ label: formatAdminLabel(key), value: formatDisplayValue(value) }];
    });
};


export default function AdminAuditPage() {
  const [tab, setTab] = useState("applications");
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    admin_query: "",
    student_number: "",
    application_status: "",
    payment_status: "",
    approval_status: "",
    action_type: "",
    failed_only: false,
    high_risk_only: false,
  });
  const [auditData, setAuditData] = useState({ tab_counts: {}, applications: [], admin_actions: [], status_changes: [], emails: [], security_access: [], high_risk: [], available_action_types: [], available_application_statuses: [], available_payment_statuses: [], available_approval_statuses: [] });
  const [status, setStatus] = useState({ loading: true, error: "" });
  // Keep the latest filter state available to stable callbacks without recreating them on every keystroke.
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const loadAudit = useCallback(async (nextFilters = filtersRef.current) => {
    setStatus({ loading: true, error: "" });
    try {
      const response = await api.get("/admin/audit/history", { params: nextFilters });
      setAuditData(response.data);
      setSelectedRow(null);
      setStatus({ loading: false, error: "" });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not load the audit history.") });
    }
  }, [filtersRef]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const currentRows = useMemo(() => {
    const rows = auditData[tab] || [];
    return rows.filter((row) => {
      if (filters.student_number && row.student_number !== filters.student_number) return false;
      if (filters.admin_query) {
        const actorText = `${row.admin_name || ""} ${row.admin_email || ""} ${row.target_record || ""}`.toLowerCase();
        if (!actorText.includes(filters.admin_query.toLowerCase())) return false;
      }
      if (filters.application_status && row.application_status !== filters.application_status) return false;
      if (filters.payment_status && row.payment_status !== filters.payment_status) return false;
      if (filters.approval_status && row.approval_status !== filters.approval_status) return false;
      if (filters.action_type && row.action_type !== filters.action_type) return false;
      if (filters.failed_only && row.delivery_status !== "failed" && row.action_type !== "admin_login_failed" && row.detail?.outcome !== "invalid_credentials" && row.detail?.outcome !== "disabled") return false;
      if (filters.high_risk_only && !row.high_risk) return false;
      return true;
    });
  }, [auditData, tab, filters]);

  const renderTable = (rows) => (
    <div className="overflow-x-auto">
      <Table data-testid={`audit-${tab}-table`}>
        <TableHeader>
          <TableRow>
            <TableHead>Date / time</TableHead>
            <TableHead>{tab === "applications" ? "Applicant / admin" : "Actor / target"}</TableHead>
            <TableHead>{tab === "emails" ? "Event" : "Action / status"}</TableHead>
            <TableHead>Student number</TableHead>
            <TableHead>Workflow / result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${tab}-${row.id}`} className="cursor-pointer" onClick={() => setSelectedRow(row)} data-testid={`audit-row-${tab}-${row.id}`}>
              <TableCell>{formatDateTime(row.timestamp)}</TableCell>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium text-slate-900">{tab === "applications" ? row.applicant_name : row.admin_name || row.target_record}</p>
                  {tab !== "applications" && row.target_record && <p className="text-xs text-slate-500">Target: {row.target_record}</p>}
                </div>
              </TableCell>
              <TableCell>{formatAdminLabel(row.action_type || row.summary)}</TableCell>
              <TableCell>{row.student_number || "—"}</TableCell>
              <TableCell>{tab === "emails" ? row.delivery_status : renderWorkflow(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!rows.length && <p className="py-8 text-sm text-slate-600" data-testid={`audit-${tab}-empty-text`}>No entries match the current filters.</p>}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="audit-history-page">
      {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="audit-history-error-message">{status.error}</p>}

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="audit-history-overview-card">
        <CardContent className="flex flex-wrap items-end justify-between gap-4 p-8">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="audit-history-eyebrow">Super admin monitoring</p>
            <h2 className="text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="audit-history-title">Audit history and accountability</h2>
            <p className="text-[16px] leading-8 text-slate-600" data-testid="audit-history-description">Track submissions, admin decisions, outbound email events, access activity, and high-risk actions from one structured monitoring page.</p>
          </div>
          <p className="text-sm leading-7 text-slate-600" data-testid="audit-history-summary-text">Use tabs and filters together to trace who changed what, when, and on which record.</p>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="audit-history-filters-card">
        <CardHeader>
          <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="audit-history-filters-title">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input type="date" value={filters.date_from} onChange={(event) => setFilters((current) => ({ ...current, date_from: event.target.value }))} data-testid="audit-filter-date-from-input" />
            <Input type="date" value={filters.date_to} onChange={(event) => setFilters((current) => ({ ...current, date_to: event.target.value }))} data-testid="audit-filter-date-to-input" />
            <Input placeholder="Admin name or email" value={filters.admin_query} onChange={(event) => setFilters((current) => ({ ...current, admin_query: event.target.value }))} data-testid="audit-filter-admin-query-input" />
            <Input placeholder="Student number" value={filters.student_number} onChange={(event) => setFilters((current) => ({ ...current, student_number: event.target.value }))} data-testid="audit-filter-student-number-input" />
            <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={filters.application_status} onChange={(event) => setFilters((current) => ({ ...current, application_status: event.target.value }))} data-testid="audit-filter-application-status-select">
              <option value="">All application statuses</option>
              {(auditData.available_application_statuses || []).map((item) => <option key={item} value={item}>{formatAdminLabel(item)}</option>)}
            </select>
            <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={filters.payment_status} onChange={(event) => setFilters((current) => ({ ...current, payment_status: event.target.value }))} data-testid="audit-filter-payment-status-select">
              <option value="">All payment statuses</option>
              {(auditData.available_payment_statuses || []).map((item) => <option key={item} value={item}>{formatAdminLabel(item)}</option>)}
            </select>
            <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={filters.approval_status} onChange={(event) => setFilters((current) => ({ ...current, approval_status: event.target.value }))} data-testid="audit-filter-approval-status-select">
              <option value="">All approval statuses</option>
              {(auditData.available_approval_statuses || []).map((item) => <option key={item} value={item}>{formatAdminLabel(item)}</option>)}
            </select>
            <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={filters.action_type} onChange={(event) => setFilters((current) => ({ ...current, action_type: event.target.value }))} data-testid="audit-filter-action-type-select">
              <option value="">All action types</option>
              {(auditData.available_action_types || []).map((item) => <option key={item} value={item}>{formatAdminLabel(item)}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="audit-filter-failed-only-row">
              <Checkbox checked={filters.failed_only} onCheckedChange={(checked) => setFilters((current) => ({ ...current, failed_only: Boolean(checked) }))} data-testid="audit-filter-failed-only-checkbox" />
              Failed events only
            </label>
            <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="audit-filter-high-risk-only-row">
              <Checkbox checked={filters.high_risk_only} onCheckedChange={(checked) => setFilters((current) => ({ ...current, high_risk_only: Boolean(checked) }))} data-testid="audit-filter-high-risk-only-checkbox" />
              High-risk actions only
            </label>
            <div className="flex gap-3">
              <Button className="rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} onClick={loadAudit} data-testid="audit-filter-apply-button">Apply filters</Button>
              <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => { const cleared = { date_from: "", date_to: "", admin_query: "", student_number: "", application_status: "", payment_status: "", approval_status: "", action_type: "", failed_only: false, high_risk_only: false }; setFilters(cleared); loadAudit(cleared); }} data-testid="audit-filter-reset-button">Reset</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4" data-testid="audit-history-tabs">
        <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-[1.25rem] bg-white/92 p-2" data-testid="audit-history-tabs-list">
          {tabConfig.map(([value, label]) => (
            <TabsTrigger key={value} value={value} className="rounded-full px-4 py-2" data-testid={`audit-tab-${value}`}>
              {label} ({auditData.tab_counts?.[value] || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        {tabConfig.map(([value]) => (
          <TabsContent key={value} value={value} data-testid={`audit-tab-content-${value}`}>
            <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
              <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid={`audit-table-card-${value}`}>
                <CardHeader>
                  <CardTitle className="text-[1.6rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950">{tabConfig.find((item) => item[0] === value)?.[1]}</CardTitle>
                </CardHeader>
                <CardContent>{status.loading ? <p className="text-sm text-slate-600" data-testid="audit-history-loading-text">Loading audit history...</p> : renderTable(value === tab ? currentRows : (auditData[value] || []))}</CardContent>
              </Card>

              <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="audit-detail-card">
                <CardHeader>
                  <CardTitle className="text-[1.6rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="audit-detail-title">Entry detail</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-7 text-slate-700">
                  {!selectedRow ? <p data-testid="audit-detail-empty-text">Select a row to inspect its detail, actor, status context, and payload metadata.</p> : (
                    <>
                      <div className="rounded-[1.1rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="audit-detail-summary-block">
                        <p className="font-semibold text-slate-900">{formatAdminLabel(selectedRow.summary || selectedRow.action_type)}</p>
                        <p>{formatDateTime(selectedRow.timestamp)}</p>
                        <p>Student number: {selectedRow.student_number || "—"}</p>
                      </div>
                      <div className="rounded-[1.1rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="audit-detail-metadata-block">
                        <div className="grid gap-3" data-testid="audit-detail-readable-list">
                          {renderDetailItems(selectedRow.detail).length ? renderDetailItems(selectedRow.detail).map((item) => (
                            <div key={`${item.label}-${item.value}`} className="rounded-[0.9rem] border border-stone-200 bg-white px-4 py-3" data-testid={`audit-detail-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{item.label}</p>
                              <p className="mt-2 text-sm leading-7 text-slate-800">{item.value}</p>
                            </div>
                          )) : <p className="text-sm text-slate-600" data-testid="audit-detail-no-extra-data-text">No additional detail is stored for this entry.</p>}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
